import { useMutation } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { type ApiError, addActionNameToMutationResult, useAxiosInstance } from 'shared/hooks/utils';
import { v4 } from 'uuid';
import { z } from 'zod/v4';

export const CalculateStepsVariablesSchema = z.object({
  invoiceIds: z.array(z.string()).nonempty(),
  creditNoteIds: z.array(z.string()),
  accountingPaymentIds: z.array(z.string()),
  thirdPartyPaymentIds: z.array(z.string()),
  deadlineAmount: z.number().min(1).optional(),
  deadlineCount: z.number().min(1).optional(),
  debtorId: z.string(),
  dueDate: z.string(),
  withFees: z.boolean(),
  delayInDays: z.number().optional(),
  delayDayInMonth: z.number().optional(),
});

const PaymentStepSchema = z.object({
  id: z.string().default(() => v4()),
  amount: z.coerce.number(),
  dueDate: z.string().transform(val => new Date(val)),
  canEdit: z.boolean(),
});

const CalculatedStepsSchema = z.object({
  totalAmount: z.coerce.number(),
  paymentSteps: z.array(PaymentStepSchema),
});

export type CalculateStepsVariables = z.infer<typeof CalculateStepsVariablesSchema>;
export type CalculatedSteps = z.infer<typeof CalculatedStepsSchema>;
export type PaymentStep = z.infer<typeof PaymentStepSchema>;

export async function calculateStepsFn(
  axiosInstance: Promise<AxiosInstance>,
  data: CalculateStepsVariables,
) {
  const instance = await axiosInstance;
  const response = await instance.post(
    '/payment_step_calculations',
    CalculateStepsVariablesSchema.parse(data),
  );

  return CalculatedStepsSchema.parse(response.data);
}

export function useCalculateSteps() {
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<CalculatedSteps, ApiError, CalculateStepsVariables>({
    mutationFn: data => calculateStepsFn(axiosInstance, data),
  });

  return addActionNameToMutationResult<
    CalculatedSteps,
    ApiError,
    CalculateStepsVariables,
    'calculateSteps'
  >('calculateSteps', mutation);
}
